"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "@/components/ui/Button";
import { ItemDetailsFormData, RequestItemFormData } from "@/core/models/requestItem/request-item.model";
import { ItemRequired } from "./ItemRequired";
import CategorySelection from "./CategorySelection";
import LocationSelection from "./LocationSelection";
import FileSelection from "./FileSelection";
import Register from "./Register";
import { authService } from "@/core/services/authService";
import { tokenService } from "@/core/services/token.service";
import { detectMobileOS } from "@/lib/utils";
import { toast } from "react-toastify";

interface RequestItemFormProps {
  formClassName?: string;
  formId?: string;
  isInline?: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
  initialData?: {
    title?: string;
    description?: string;
    serviceId?: string;
    categoryId?: string;
    subCategoryId?: string;
    domainCategoryId?: string;
  };
}

const initialFormState: RequestItemFormData = {};
const formRegistry = new Set<string>();

export default function RequestItemForm({ 
  formClassName,
  formId = 'default',
  isInline = false,
  onSubmittingChange,
  initialData 
}: RequestItemFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<RequestItemFormData>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const onSubmittingChangeRef = useRef(onSubmittingChange);
  useEffect(() => { onSubmittingChangeRef.current = onSubmittingChange; }, [onSubmittingChange]);

  const resetForm = () => {
    setFormData(initialFormState);
    setCurrentStep(1);
  };

  // Prevent duplicate forms with the same ID
  useEffect(() => {
    if (formRegistry.has(formId)) {
      console.warn(`⚠️ Duplicate RequestItemForm with formId: ${formId}`);
      return;
    }
    
    formRegistry.add(formId);
        
    return () => {
      formRegistry.delete(formId);      
    };
  }, [formId]);

  // Only use initialData for popup forms, not embedded forms
  const shouldUseInitialData = formId.startsWith('popup-') && initialData;

  const [itemDetailsData, setItemDetailsData] = useState<ItemDetailsFormData>({
    item: shouldUseInitialData ? (initialData?.title || '') : '',
    description: shouldUseInitialData ? (initialData?.description || 'Vehicle Make :\nVehicle Model :\nVIN No. :') : 'Vehicle Make :\nVehicle Model :\nVIN No. :',
  });

  // Update when initialData changes, but only for popup forms
  useEffect(() => {
    if (shouldUseInitialData) {     
      setItemDetailsData(prev => ({
        ...prev,
        item: initialData.title || prev.item,
        description: initialData.description || prev.description,
      }));
      
      // Also update the main form data
      setFormData(prev => ({
        ...prev,
        item: initialData.title || prev.item,
        description: initialData.description || prev.description,
      }));
    }
  }, [initialData, shouldUseInitialData, formId]);


  const handleNext = () => setCurrentStep((prev) => prev + 1);
  const handlePrev = () => setCurrentStep((prev) => Math.max(1, prev - 1));

  const handleUpdate = useCallback(
    (newData: Partial<RequestItemFormData>) => {      
      setFormData((prev) => ({ ...prev, ...newData }));
      
      // If it's item details, also update itemDetailsData
      if ('item' in newData || 'description' in newData) {
        setItemDetailsData(prev => ({
          ...prev,
          item: newData.item || prev.item,
          description: newData.description || prev.description,
        }));
      }
    },
    []
  );

  const postRequest = async (data: RequestItemFormData) => {
    setIsSubmitting(true);
    onSubmittingChangeRef.current?.(true);
    setSubmitProgress(20);
    try {
      setSubmitProgress(50);
      const now = new Date().toISOString();
      const userId = tokenService.getUserId();
      const files = data.upload && data.upload.length > 0 ? data.upload : null;
      const result = await authService.postItemRequest(
        {
          itemRequestTitle: data.item,
          itemRequestDescription: data.description,
          itemRequestDate: now,
          categoryId: data.categoryId,
          suburbId: data.suburbId,
          provinceId: data.provinceId,
          cityId: data.cityId,
          requestApprovedMail: false,
          createdBy: userId || null,
          createdDate: now,
          modifiedDate: now,
        },
        files
      );
      setSubmitProgress(90);
      if (result?.success) {
        setSubmitProgress(100);
        setCurrentStep(1);
        toast.success("Successfully submitted the item request");
      }
      return result?.success ?? false;
    } catch (error) {
      console.error("Failed to submit item request", error);
      toast.error("Failed to submit your request. Please try again.");
      return false;
    } finally {
      setIsSubmitting(false);
      onSubmittingChangeRef.current?.(false);
      setSubmitProgress(0);
    }
  };
  
  const handleSubmit = async (finalData: Partial<RequestItemFormData>) => {
    const data = { ...formData, ...finalData };
    const token = "Basic " + window.btoa(`${data.email}:${data.password}`);
    
    try {
      const isEmailExists = await authService.emailCheck(data.email ?? '');
      if (isEmailExists === true) {
        const loginData = await authService.login(token);
        if (loginData && loginData?.token) {
          tokenService.setLoggedUserDetail(loginData.token, loginData.tokenExpires, loginData.firstName, loginData.lastName);

          if (await postRequest(data)) setCurrentStep(6);
        }
        return;
      }

      const isMobileExists = await authService.mobileCheck(data.mobile ?? '');
      if (isMobileExists !== true) {
        console.warn("Mobile already exists or invalid.");
        return;
      }

      const registerResult = await authService.registerUser({
        email: data.email ?? "",
        contactNo: data.mobile ?? "",
        userName: data.name ?? "",
        password: data.password ?? "",
        firstName: data.name ?? "",
        lastName: "",
        provinceID: data.provinceId ?? 0,
        cityID: data.cityId ?? 0,
        suburbID: data.suburbId ?? 0,
        domainUrl: "2",
        track: 1
      }, detectMobileOS() === "Unknown" ? 1 : 0);

      if (registerResult?.token) {
        const loginData = await authService.login(token);
        if (loginData && loginData?.token) {
          tokenService.setLoggedUserDetail(loginData.token, loginData.tokenExpires, loginData.firstName, loginData.lastName);

          if (await postRequest(data)) setCurrentStep(6);
        }
      }
    } catch (error) {
      console.error("Submission Error:", error);
    }
  };

  // FIXED: Single render with proper step management
  return (
    <div className={`${isInline ? 'w-full relative' : 'xl:absolute w-full top-0 xl:-top-12 h-full relative'}`}>
      {isSubmitting && (
        <div className="absolute inset-0 z-[1400] flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="w-3/4 max-w-xs">
            <p className="text-sm font-semibold text-center text-gray-700 mb-3">
              Processing your request, please wait&hellip;
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-[var(--primary-color)] h-2.5 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${submitProgress}%` }}
              />
            </div>
            <p className="text-xs text-center text-gray-400 mt-2">{submitProgress}%</p>
          </div>
        </div>
      )}
      <div className="hidden xl:flex h-9 md:h-12 bg-primary text-white text-md items-center pl-4 font-semibold relative z-1 before:absolute before:h-full before:w-full before:-left-50 before:top-0 before:bg-[var(--primary-color)] before:-z-1 triangle">
        Request Car Parts Here in 2 min!
      </div>
      {/* Stepper — hidden on success step */}
      {currentStep < 6 && (
        <div className="flex items-center justify-center gap-0 px-3 py-2 bg-white border-b border-gray-200">
          {[
            { n: 1, label: "Item" },
            { n: 2, label: "Category" },
            { n: 3, label: "Location" },
            { n: 4, label: "Files" },
            { n: 5, label: "Register" },
          ].map(({ n, label }, idx, arr) => (
            <React.Fragment key={n}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors duration-200 ${
                    n < currentStep
                      ? "bg-[var(--primary-color)] border-[var(--primary-color)] text-white"
                      : n === currentStep
                      ? "bg-white border-[var(--primary-color)] text-[var(--primary-color)]"
                      : "bg-white border-gray-300 text-gray-500"
                  }`}
                >
                  {n < currentStep ? "✓" : n}
                </div>
                <span
                  className={`text-[10px] mt-0.5 leading-tight ${
                    n <= currentStep ? "text-[var(--primary-color)] font-semibold" : "text-gray-500"
                  }`}
                >
                  {label}
                </span>
              </div>
              {idx < arr.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-1 mb-4 transition-colors duration-200 ${
                    n < currentStep ? "bg-[var(--primary-color)]" : "bg-gray-200"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className={`h-full md:px-1 md:pt-1 max-md:mx-[15px] mt-2 md:mt-0 ${formClassName}`}>
        
        {/* Step 1: Item Required */}
        {currentStep === 1 && (
          <ItemRequired
            onUpdate={handleUpdate}
            handleNext={handleNext}
            initialValues={{
              item: formData.item || itemDetailsData.item || '',
              description: formData.description || itemDetailsData.description || 'Vehicle Make :\nVehicle Model :\nVIN No. :'
            }}
            formClassName={formClassName}
            formId={formId}
          />
        )}

        {/* Step 2: Category Selection */}
        {currentStep === 2 && (
          <CategorySelection
            onUpdate={handleUpdate}
            handleNext={handleNext}
            handlePrev={handlePrev}
            formClassName={formClassName}
            initialValues={{ categoryId: formData.categoryId }}
          />
        )}

        {/* Step 3: Location Selection */}
        {currentStep === 3 && (
          <LocationSelection
            onUpdate={handleUpdate}
            handleNext={handleNext}
            handlePrev={handlePrev}
            formClassName={formClassName}
            initialValues={{
              provinceId: formData.provinceId,
              cityId: formData.cityId,
              suburbId: formData.suburbId,
            }}
          />
        )}

        {/* Step 4: File Selection */}
        {currentStep === 4 && (
          <FileSelection
            onUpdate={handleUpdate}
            handleNext={handleNext}
            handlePrev={handlePrev}
            formClassName={formClassName}
            initialValues={{ upload: formData.upload || [] }}
          />
        )}

        {/* Step 5: Register */}
        {currentStep === 5 && (
          <Register
            onUpdate={handleUpdate}
            handleSubmit={handleSubmit}
            handlePrev={handlePrev}
            formClassName={formClassName}
            isSubmitting={isSubmitting}
            initialValues={{
              name: formData.name,
              password: formData.password,
              email: formData.email,
              mobile: formData.mobile,
            }}
          />
        )}

        {/* Step 6: Success */}
        {currentStep === 6 && (
          <div className="text-center p-4">
            <h2 className="text-2xl font-bold">Thank You!</h2>
            <p>Your request has been submitted successfully.</p>
            <Button
              onClick={resetForm}
              className="bg-[var(--primary-color)] border border-[var(--primary-color)] text-sm text-white hover:bg-white hover:text-[var(--primary-color)] mt-4"
            >
              Submit Another Request
            </Button>
          </div>
        )}

      </div>
    </div>
  );
}